import Head from "next/head";
import { useEffect } from "react";

import { getAnalytics, PageName } from "@tm/insurance/analytics";
import { akamaiGetDevice } from "@tm/server-utils";
import { withDeviceAndThemeProvider } from "@tm/ui-widgets";
import { withPartnerInsuranceHeader } from "@tm/ui/hoc";

import {
  getPageNameAnalytics,
  initializeErrorPageTracking,
} from "@tm/insurance/analytics/xdm";
import { PARTNER } from "@tm/isomorphic-utils";
import { ErrorPage } from "..";

export interface ErrorScreenProps {
  error?:
    | {
        StatusCode?: number;
        code?: string;
        message?: {
          title?: string;
          description?: string;
          icon?: string;
        };
      }
    | string;
  trackError?: boolean;
  pageName?: PageName;
  statusCode?: string | undefined;
}

const analytics = getAnalytics();

export const ErrorScreen = ({
  error,
  trackError = false,
  pageName,
  statusCode,
}: ErrorScreenProps) => {
  useEffect(() => {
    if (trackError && pageName && error && typeof error !== "string") {
      analytics?.trackPageLoadErrorEvent({
        pageName,
        actionType: "page_load_error",
        error: `${error?.message?.title}|
                  ${error?.message?.description}`,
      });

      const pageNameAnalytics = getPageNameAnalytics(pageName);
      initializeErrorPageTracking({
        page_context: {
          page_name: pageNameAnalytics,
          page_type: pageNameAnalytics === "landing" ? "landing" : "funnel",
          funnel_step:
            pageNameAnalytics === "landing" ? "landing" : pageNameAnalytics,
        },
        event_detail: {
          event_value: "error",
          event_name: "error",
          event_type: "action",
        },
        error_details_list: [
          {
            message: `${error?.message?.title}|
                    ${error?.message?.description}`,
            code: (error?.code as string) || statusCode?.toString(),
            source: "error_screen",
            severity: 2,
          },
        ],
      });
    }
  }, [error, pageName, trackError]);

  const title =
    (typeof error !== "string" && error?.message?.title) || "Sorry!";
  const description =
    (typeof error !== "string" && error?.message?.description) ||
    "We are having trouble communicating with the servers. Please try again.";
  const icon =
    (typeof error !== "string" && error?.message?.icon) ||
    "https://tripmoneycmsimgak.mmtcdn.com/img/error_Img_1e80a42832.svg";

  return (
    <>
      <Head>
        <title>Tripmoney Error</title>
      </Head>
      {withDeviceAndThemeProvider(
        withPartnerInsuranceHeader(
          <ErrorPage
            title={title}
            info={description}
            errorImg={icon}
            headerComponent={<></>}
          />
        ),
        akamaiGetDevice({}),
        { pageTheme: PARTNER.MMT }
      )}
    </>
  );
};
